import React, { useMemo, useEffect } from 'react';
import { useFormContext, RegisterOptions, Controller } from 'react-hook-form';
import { useTranslation } from '@app/core/i18n';

import { Input } from '@ast/magma/components/input';

import { FieldBehaviorMode } from '@app/queryTyping';

import { Fields } from '@app/common/configurable-wizards/types';
import { combineNames, findFieldNode, getFieldErrorMessage } from '@app/common/configurable-wizards/utils';
import { useCommonValidationMessages } from '@app/common/valiation/commonValidationMessages';

import { IGNORE_SERIALIZE_GROUP_KEY } from '../../utils/serializeResult';
import { CFFormField } from '../common/CFFormField';

import { PasswordValidationRules } from './types';

export interface PasswordConfirmationInputProps {
  field: Fields['Password'];
  passwordFieldName: string;
}

export const PasswordConfirmationInput: React.FC<PasswordConfirmationInputProps> = ({
  field,
  passwordFieldName,
}) => {
  const { t } = useTranslation();
  const {
    control,
    getValues,
    trigger,
    watch,
    formState: { errors, dirtyFields },
    setValue,
  } = useFormContext();

  const name = combineNames(IGNORE_SERIALIZE_GROUP_KEY, `Confirm${passwordFieldName}`);
  const commonValidationMessages = useCommonValidationMessages();
  const error = getFieldErrorMessage(name, errors, commonValidationMessages);
  const originalPasswordValue = watch(passwordFieldName);

  useEffect(() => {
    // check if confirm-passsword fied was already changed
    const isDirty = findFieldNode<boolean>(name, dirtyFields);

    // than validate when original password is changing
    if (isDirty && originalPasswordValue) {
      trigger(name);
    }
  }, [originalPasswordValue]);

  const registerOptions = useMemo<RegisterOptions>(() => {
    const rules: RegisterOptions = {
      [PasswordValidationRules.required]: field.fieldBehaviorMode === FieldBehaviorMode.REQUIRED,
    };

    rules.validate = {
      matchesPreviousPassword: (value) => getValues(passwordFieldName) === value || t(
        'validation.password-match|Password-matching validation message',
        'Passwords do not match',
      ) as string,
    };

    return rules;
  }, [field]);

  return (
    <Controller
      shouldUnregister
      render={(renderProps) => (
        <CFFormField error={error}>
          <Input
            label={t('new-password.confirm-password-input.label|Confirm password input label', 'Confirm password')}
            aria-invalid={!!error}
            type="password"
            autoComplete="new-password"
            data-stable-name={`${field.id}ConfirmationInput`}
            {...renderProps.field}
            value={renderProps.field.value || ''}
            onBlur={(event) => {
              setValue(name, event.target.value?.trim());
            }}
            // validate confirm-password input immediately during input event
            onInput={() => { setTimeout(() => trigger(name)); }}
          />
        </CFFormField>
      )}
      name={name}
      control={control}
      rules={registerOptions}
    />
  );
};
